import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_CONST } from "../../../const";
import {
  Table,
  Button,
  Input,
  Select,
  Tag,
  Dropdown,
  Modal,
  message,
  Empty,
  Descriptions,
  Form,
  InputNumber,
  Upload,
  Flex,
} from "antd";
import {
  SearchOutlined,
  MoreOutlined,
  DollarOutlined,
  FileTextOutlined,
  DownloadOutlined,
  EyeOutlined,
  CheckOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  EditOutlined,
  SendOutlined,
  SyncOutlined,
  LineChartOutlined,
  InboxOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import {
  FinanceContainer,
  PageHeader,
  MetricsSection,
  MetricCard,
  TopSection,
  FiltersRow,
  TabsContainer,
  TableContainer,
  EmptyState,
  InsightsGrid,
  InsightCard,
} from "./Finance.style";

const { Option } = Select;
const { Dragger } = Upload;

const Finance = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Partner Payables");
  const [searchText, setSearchText] = useState("");
  const [filterPartner, setFilterPartner] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPeriod, setFilterPeriod] = useState(""); // For Financial Insights
  
  // Create Invoice Modal state (for customer invoices)
  const [createInvoiceModalVisible, setCreateInvoiceModalVisible] = useState(false);
  const [selectedInvoiceRecord, setSelectedInvoiceRecord] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceNotes, setInvoiceNotes] = useState("");
  const [uploadedInvoiceFile, setUploadedInvoiceFile] = useState(null);
  
  // Partner Invoice Modal state (for admin to upload/modify partner invoices)
  const [partnerInvoiceModalVisible, setPartnerInvoiceModalVisible] = useState(false);
  const [selectedPayableRecord, setSelectedPayableRecord] = useState(null);
  const [partnerInvoiceNumber, setPartnerInvoiceNumber] = useState("");
  const [partnerInvoiceNotes, setPartnerInvoiceNotes] = useState("");
  const [uploadedPartnerInvoiceFile, setUploadedPartnerInvoiceFile] = useState(null);
  
  // Modify Fee Modal state
  const [modifyFeeModalVisible, setModifyFeeModalVisible] = useState(false);
  const [selectedFeeRecord, setSelectedFeeRecord] = useState(null);
  const [clientFeePercentage, setClientFeePercentage] = useState(10); // Default 10% markup
  const [partnerFeePercentage, setPartnerFeePercentage] = useState(5); // Default 5% deduction
  
  // Loading states
  const [loadingPayables, setLoadingPayables] = useState(false);
  const [loadingReceivables, setLoadingReceivables] = useState(false);
  const [loadingReconciliation, setLoadingReconciliation] = useState(false);
  const [loadingRevenueAnalysis, setLoadingRevenueAnalysis] = useState(false);
  const [loadingFinancialInsights, setLoadingFinancialInsights] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);

  // Client Billing (Receivables) from API
  const [receivables, setReceivables] = useState([]);

  // Partner Payables (from invoices API)
  const [payables, setPayables] = useState([]);

  // Invoice Reconciliation data from API
  const [reconciliation, setReconciliation] = useState([]);
  
  // Revenue Analysis data from API
  const [revenueAnalysis, setRevenueAnalysis] = useState([]);
  
  // Financial Insights data from API
  const [financialInsights, setFinancialInsights] = useState({ summary: {}, periodData: [], topCustomersByRevenue: [], topPartnersByVolume: [], topCustomersByTalents: [], topPartnersByTalents: [] });

  // Fetch Partner Payables (invoices) from API
  const fetchPayables = useCallback(async () => {
    setLoadingPayables(true);
    try {
      const token = localStorage.getItem("authToken");
      const body = {};
      if (filterMonth) {
        const monthMap = {
          "January": 1, "February": 2, "March": 3, "April": 4, "May": 5, "June": 6,
          "July": 7, "August": 8, "September": 9, "October": 10, "November": 11, "December": 12
        };
        body.month = monthMap[filterMonth];
      }
      if (filterYear) body.year = parseInt(filterYear);
      if (filterStatus) body.partnerStatus = filterStatus.toLowerCase();
      
      const response = await axios.post(API_CONST.GET_INVOICES, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.invoices) {
        const transformed = response.data.invoices.map(inv => ({
          id: inv.id,
          recordId: inv.id,
          partnerInvoiceId: inv.partnerInvoiceNumber,
          clientInvoiceId: inv.customerInvoiceNumber,
          talentId: inv.talentId,
          talentName: inv.talentName || 'Unknown',
          role: inv.jobRole || '-',
          partner: inv.partnerOrganization,
          customer: inv.customerOrganization,
          jobId: inv.jobId,
          month: inv.monthName,
          year: String(inv.year),
          days: inv.billableHours ? Math.round(inv.billableHours / 8) : 0,
          hours: inv.billableHours || 0,
          partnerAmount: parseFloat(inv.partnerSubtotal) || 0,
          partnerGst: parseFloat(inv.partnerTaxAmount) || 0,
          partnerTotal: parseFloat(inv.partnerTotalAmount) || 0,
          clientAmount: inv.customerSubtotal,
          clientGst: inv.customerTaxAmount || 0,
          clientTotal: inv.customerTotalAmount,
          partnerStatus: inv.partnerStatus ? inv.partnerStatus.charAt(0).toUpperCase() + inv.partnerStatus.slice(1) : 'Pending',
          clientStatus: inv.customerStatus ? inv.customerStatus.charAt(0).toUpperCase() + inv.customerStatus.slice(1) : 'Draft',
          timesheetStatus: inv.timesheetStatus ? inv.timesheetStatus.charAt(0).toUpperCase() + inv.timesheetStatus.slice(1) : 'Pending',
          submittedOn: inv.partnerInvoiceSubmittedAt ? new Date(inv.partnerInvoiceSubmittedAt).toISOString().split('T')[0] : '',
          dueDate: inv.dueDate ? new Date(inv.dueDate).toISOString().split('T')[0] : '',
          partnerNotes: inv.partnerNotes || '',
        }));
        setPayables(transformed);
      }
    } catch (error) {
      console.error("Error fetching payables:", error);
      message.error("Failed to load partner payables");
    } finally {
      setLoadingPayables(false);
    }
  }, [filterMonth, filterYear, filterStatus]);

  useEffect(() => {
    fetchPayables();
  }, [fetchPayables]);

  // Fetch Client Billing (Receivables) from API
  const fetchReceivables = useCallback(async () => {
    setLoadingReceivables(true);
    try {
      const token = localStorage.getItem("authToken");
      const body = {};
      if (filterMonth) {
        const monthMap = {
          "January": 1, "February": 2, "March": 3, "April": 4, "May": 5, "June": 6,
          "July": 7, "August": 8, "September": 9, "October": 10, "November": 11, "December": 12
        };
        body.month = monthMap[filterMonth];
      }
      if (filterYear) body.year = parseInt(filterYear);
      if (filterStatus) body.customerStatus = filterStatus.toLowerCase();
      
      const response = await axios.post(API_CONST.GET_INVOICES, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.invoices) {
        const transformed = response.data.invoices.map(inv => ({
          id: inv.id,
          recordId: inv.id,
          partnerInvoiceId: inv.partnerInvoiceNumber || '',
          customerInvoiceId: inv.customerInvoiceNumber,
          talentId: inv.talentId,
          talentName: inv.talentName || 'Unknown',
          partner: inv.partnerOrganization || '',
          customer: inv.customerOrganization || '',
          jobId: inv.jobId,
          month: inv.monthName,
          year: String(inv.year),
          days: inv.billableHours ? Math.round(inv.billableHours / 8) : 0,
          hours: inv.billableHours || 0,
          amount: parseFloat(inv.customerSubtotal) || 0,
          gst: parseFloat(inv.customerTaxAmount) || 0,
          totalAmount: parseFloat(inv.customerTotalAmount) || 0,
          invoiceStatus: inv.customerStatus ? inv.customerStatus.charAt(0).toUpperCase() + inv.customerStatus.slice(1) : 'Draft',
          timesheetStatus: inv.timesheetStatus ? inv.timesheetStatus.charAt(0).toUpperCase() + inv.timesheetStatus.slice(1) : 'Pending',
          dueDate: inv.dueDate ? new Date(inv.dueDate).toISOString().split('T')[0] : '',
          customerNotes: inv.customerNotes || '',
        }));
        setReceivables(transformed);
      }
    } catch (error) {
      console.error("Error fetching receivables:", error);
      message.error("Failed to load client billing data");
    } finally {
      setLoadingReceivables(false);
    }
  }, [filterMonth, filterYear, filterStatus]);

  useEffect(() => {
    fetchReceivables();
  }, [fetchReceivables]);

  // Fetch Invoice Reconciliation data from API
  const fetchReconciliation = useCallback(async () => {
    setLoadingReconciliation(true);
    try {
      const token = localStorage.getItem("authToken");
      const body = {};
      if (filterMonth) {
        const monthMap = {
          "January": 1, "February": 2, "March": 3, "April": 4, "May": 5, "June": 6,
          "July": 7, "August": 8, "September": 9, "October": 10, "November": 11, "December": 12
        };
        body.month = monthMap[filterMonth];
      }
      if (filterYear) body.year = parseInt(filterYear);
      
      const response = await axios.post(API_CONST.GET_INVOICES, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.invoices) {
        const transformed = response.data.invoices.map(inv => ({
          id: inv.id,
          recordId: inv.id,
          partnerInvoiceId: inv.partnerInvoiceNumber,
          customerInvoiceId: inv.customerInvoiceNumber,
          talentId: inv.talentId,
          talentName: inv.talentName || 'Unknown',
          partner: inv.partnerOrganization,
          customer: inv.customerOrganization,
          jobId: inv.jobId,
          month: inv.monthName,
          year: String(inv.year),
          billableHours: inv.billableHours,
          partnerTotal: inv.partnerTotalAmount,
          clientTotal: inv.customerTotalAmount,
          receivableStatus: inv.customerStatus ? inv.customerStatus.charAt(0).toUpperCase() + inv.customerStatus.slice(1) : 'Draft',
          payableStatus: inv.partnerStatus ? inv.partnerStatus.charAt(0).toUpperCase() + inv.partnerStatus.slice(1) : 'Pending',
          baseCost: inv.partnerSubtotal,
          clientFeePercentage: 10,
          partnerFeePercentage: 5,
        }));
        setReconciliation(transformed);
      }
    } catch (error) {
      console.error("Error fetching reconciliation data:", error);
    } finally {
      setLoadingReconciliation(false);
    }
  }, [filterMonth, filterYear]);

  useEffect(() => {
    if (activeTab === "Invoice Reconciliation") {
      fetchReconciliation();
    }
  }, [activeTab, fetchReconciliation]);

  // Fetch Revenue Analysis data from API
  const fetchRevenueAnalysis = useCallback(async () => {
    setLoadingRevenueAnalysis(true);
    try {
      const token = localStorage.getItem("authToken");
      const body = {};
      if (filterMonth) {
        const monthMap = {
          "January": 1, "February": 2, "March": 3, "April": 4, "May": 5, "June": 6,
          "July": 7, "August": 8, "September": 9, "October": 10, "November": 11, "December": 12
        };
        body.month = monthMap[filterMonth];
      }
      if (filterYear) body.year = parseInt(filterYear);
      if (filterPartner) body.partner = filterPartner;
      if (filterCustomer) body.customer = filterCustomer;

      const response = await axios.post(API_CONST.REVENUE_ANALYSIS, body, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.data) {
        setRevenueAnalysis(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching revenue analysis:", error);
      message.error("Failed to load revenue analysis data");
    } finally {
      setLoadingRevenueAnalysis(false);
    }
  }, [filterMonth, filterYear, filterPartner, filterCustomer]);

  useEffect(() => {
    if (activeTab === "Revenue Analysis") {
      fetchRevenueAnalysis();
    }
  }, [activeTab, fetchRevenueAnalysis]);

  // Fetch Financial Insights data from API
  const fetchFinancialInsights = useCallback(async () => {
    setLoadingFinancialInsights(true);
    try {
      const token = localStorage.getItem("authToken");
      const body = {};
      if (filterPeriod) body.period = filterPeriod;
      if (filterYear) body.year = filterYear;

      const response = await axios.post(API_CONST.FINANCIAL_INSIGHTS, body, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        setFinancialInsights(response.data);
      }
    } catch (error) {
      console.error("Error fetching financial insights:", error);
      message.error("Failed to load financial insights");
    } finally {
      setLoadingFinancialInsights(false);
    }
  }, [filterPeriod, filterYear]);

  useEffect(() => {
    if (activeTab === "Financial Insights") {
      fetchFinancialInsights();
    }
  }, [activeTab, fetchFinancialInsights]);

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem("authToken");
      const now = new Date();
      await axios.post(API_CONST.SYNC_NOW, {
        month: now.getMonth() + 1,
        year: now.getFullYear()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      message.success("Sync completed successfully");
      fetchPayables();
      fetchReceivables();
      fetchReconciliation();
    } catch (error) {
      console.error("Error syncing:", error);
      message.error(error.response?.data?.message || "Failed to sync");
    } finally {
      setSyncing(false);
    }
  };

  // Get filtered data functions
  const getFilteredReceivables = () => {
    let filtered = [...receivables];

    if (searchText) {
      filtered = filtered.filter(
        (r) =>
          (r.talentName || '').toLowerCase().includes(searchText.toLowerCase()) ||
          (r.customer || '').toLowerCase().includes(searchText.toLowerCase()) ||
          (r.talentId || '').toLowerCase().includes(searchText.toLowerCase()) ||
          (r.customerInvoiceId || '').toLowerCase().includes(searchText.toLowerCase()) ||
          (r.partnerInvoiceId || '').toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (filterPartner) filtered = filtered.filter((r) => r.partner === filterPartner);
    if (filterCustomer) filtered = filtered.filter((r) => r.customer === filterCustomer);
    if (filterMonth) filtered = filtered.filter((r) => r.month === filterMonth);
    if (filterStatus) filtered = filtered.filter((r) => r.invoiceStatus === filterStatus);

    return filtered;
  };

  const getFilteredPayables = () => {
    let filtered = [...payables];

    if (searchText) {
      filtered = filtered.filter(
        (p) =>
          (p.talentName || '').toLowerCase().includes(searchText.toLowerCase()) ||
          (p.partner || '').toLowerCase().includes(searchText.toLowerCase()) ||
          (p.partnerInvoiceId || '').toLowerCase().includes(searchText.toLowerCase()) ||
          (p.talentId || '').toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (filterPartner) filtered = filtered.filter((p) => p.partner === filterPartner);

    return filtered;
  };

  const getFilteredReconciliation = () => {
    let filtered = [...reconciliation];

    if (searchText) {
      filtered = filtered.filter(
        (r) =>
          r.talentName.toLowerCase().includes(searchText.toLowerCase()) ||
          r.partner.toLowerCase().includes(searchText.toLowerCase()) ||
          r.customer.toLowerCase().includes(searchText.toLowerCase()) ||
          r.talentId.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (filterPartner) filtered = filtered.filter((r) => r.partner === filterPartner);
    if (filterCustomer) filtered = filtered.filter((r) => r.customer === filterCustomer);
    if (filterMonth) filtered = filtered.filter((r) => r.month === filterMonth);
    if (filterStatus) {
      filtered = filtered.filter(
        (r) => r.receivableStatus === filterStatus || r.payableStatus === filterStatus
      );
    }

    return filtered;
  };

  const getFilteredRevenueAnalysis = () => {
    let filtered = [...revenueAnalysis];

    if (searchText) {
      filtered = filtered.filter(
        (r) =>
          r.talentName.toLowerCase().includes(searchText.toLowerCase()) ||
          r.partner.toLowerCase().includes(searchText.toLowerCase()) ||
          r.customer.toLowerCase().includes(searchText.toLowerCase()) ||
          r.talentId.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (filterPartner) filtered = filtered.filter((r) => r.partner === filterPartner);
    if (filterCustomer) filtered = filtered.filter((r) => r.customer === filterCustomer);
    if (filterMonth) filtered = filtered.filter((r) => r.month === filterMonth);

    return filtered;
  };

  // Calculate metrics
  const getReceivableMetrics = () => {
    const today = new Date();
    const totalAmount = receivables
      .filter((r) => r.invoiceStatus === "Paid")
      .reduce((sum, r) => sum + r.totalAmount, 0);
    const currentReceivable = receivables
      .filter((r) => !["Paid", "Overdue", "Cancelled", "Rejected"].includes(r.invoiceStatus) && new Date(r.dueDate) >= today)
      .reduce((sum, r) => sum + r.totalAmount, 0);
    const overdueAmount = receivables
      .filter(
        (r) =>
          r.invoiceStatus === "Overdue" ||
          (!["Paid", "Cancelled", "Rejected"].includes(r.invoiceStatus) && new Date(r.dueDate) < today)
      )
      .reduce((sum, r) => sum + r.totalAmount, 0);
    const totalInvoices = receivables.length;

    return { totalAmount, currentReceivable, overdueAmount, totalInvoices };
  };

  const getPayableMetrics = () => {
    const totalPaid = payables
      .filter((p) => p.partnerStatus === "Paid")
      .reduce((sum, p) => sum + (p.partnerTotal || 0), 0);
    const currentPayable = payables
      .filter((p) => ["Submitted", "Verified", "Approved", "Pending"].includes(p.partnerStatus))
      .reduce((sum, p) => sum + (p.partnerTotal || 0), 0);
    const overduePayable = payables
      .filter((p) => p.partnerStatus === "Overdue")
      .reduce((sum, p) => sum + (p.partnerTotal || 0), 0);
    const totalInvoices = payables.length;

    return { totalPaid, currentPayable, overduePayable, totalInvoices };
  };

  const getReconciliationMetrics = () => {
    const totalInvoicePaid = reconciliation.filter(
      (r) => r.receivableStatus === "Paid"
    ).length;
    const totalBillsPaid = reconciliation.filter((r) => r.payableStatus === "Paid").length;
    const receivablePending = reconciliation.filter(
      (r) => r.receivableStatus !== "Paid"
    ).length;
    const payablePending = reconciliation.filter((r) => r.payableStatus !== "Paid").length;

    return { totalInvoicePaid, totalBillsPaid, receivablePending, payablePending };
  };

  const getRevenueMetrics = () => {
    const totalRevenue = revenueAnalysis.reduce((sum, r) => sum + r.revenue, 0);
    const totalReceivable = revenueAnalysis.reduce((sum, r) => sum + r.amountReceivable, 0);
    const totalPayable = revenueAnalysis.reduce((sum, r) => sum + r.amountPayable, 0);
    const totalGSTIn = revenueAnalysis.reduce((sum, r) => sum + r.gstIn, 0);
    const totalGSTOut = revenueAnalysis.reduce((sum, r) => sum + r.gstOut, 0);

    return {
      totalRevenue,
      totalReceivable,
      totalPayable,
      totalGSTIn,
      totalGSTOut,
    };
  };

  // Action handlers for Client Billing
  const handleViewBreakup = (record) => {
    Modal.info({
      title: `Client Invoice Details - ${record.customerInvoiceId || 'N/A'}`,
      width: 600,
      content: (
        <Descriptions bordered column={1} size="small" style={{ marginTop: 16 }}>
          <Descriptions.Item label="Record ID">{record.recordId?.substring(0, 12)}...</Descriptions.Item>
          <Descriptions.Item label="Partner Invoice Ref">{record.partnerInvoiceId || '-'}</Descriptions.Item>
          <Descriptions.Item label="Client Invoice">{record.customerInvoiceId || '-'}</Descriptions.Item>
          <Descriptions.Item label="Talent Name">{record.talentName}</Descriptions.Item>
          <Descriptions.Item label="Customer">{record.customer}</Descriptions.Item>
          <Descriptions.Item label="Job ID">{record.jobId}</Descriptions.Item>
          <Descriptions.Item label="Month">
            {record.month} {record.year}
          </Descriptions.Item>
          <Descriptions.Item label="Days">{record.days}</Descriptions.Item>
          <Descriptions.Item label="Hours">{record.hours}</Descriptions.Item>
          <Descriptions.Item label="Amount">
            ₹ {(record.amount || 0).toLocaleString("en-IN")}
          </Descriptions.Item>
          <Descriptions.Item label="GST (18%)">
            ₹ {(record.gst || 0).toLocaleString("en-IN")}
          </Descriptions.Item>
          <Descriptions.Item label="Total Amount">
            <strong>₹ {(record.totalAmount || 0).toLocaleString("en-IN")}</strong>
          </Descriptions.Item>
          <Descriptions.Item label="Invoice Status">
            <Tag
              color={
                record.invoiceStatus === "Paid" || record.invoiceStatus === "Approved"
                  ? "green"
                  : record.invoiceStatus === "Sent"
                  ? "blue"
                  : record.invoiceStatus === "Overdue"
                  ? "red"
                  : "orange"
              }
            >
              {record.invoiceStatus}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Notes">{record.customerNotes || '-'}</Descriptions.Item>
        </Descriptions>
      ),
    });
  };

  const handleApproveReceivable = (record) => {
    Modal.confirm({
      title: "Approve Customer Invoice",
      content: `Approve invoice ${record.customerInvoiceId || record.id} for ${record.customer}? This will enable the receivable to be marked as paid.`,
      okText: "Approve",
      okButtonProps: { style: { background: "#00d9a9", borderColor: "#00d9a9" } },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.put(
            `${API_CONST.UPDATE_INVOICE_STATUS}/${record.id}`,
            { statusType: "customer", status: "approved" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success(`Invoice approved for ${record.customer}`);
          fetchReceivables();
        } catch (error) {
          console.error("Error approving invoice:", error);
          message.error(error.response?.data?.message || "Failed to approve invoice");
        }
      },
    });
  };

  const handleDownloadTimesheet = async (record) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_TIMESHEET_DOWNLOAD_URL,
        { invoiceId: record.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data?.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      } else {
        message.warning("Timesheet file not available");
      }
    } catch (error) {
      console.error("Error downloading timesheet:", error);
      message.error(error.response?.data?.message || "Failed to download timesheet");
    }
  };

  const handleCreateInvoice = (record) => {
    setSelectedInvoiceRecord(record);
    setCreateInvoiceModalVisible(true);
    setInvoiceNumber(record.customerInvoiceId || "");
    setInvoiceNotes(record.customerNotes || "");
  };

  const handleCloseInvoiceModal = () => {
    setCreateInvoiceModalVisible(false);
    setSelectedInvoiceRecord(null);
    setInvoiceNumber("");
    setInvoiceNotes("");
    setUploadedInvoiceFile(null);
  };

  const handleSubmitInvoice = async () => {
    if (!uploadedInvoiceFile) {
      message.warning("Please upload an invoice file");
      return;
    }
    if (!invoiceNumber.trim()) {
      message.warning("Please enter an invoice number");
      return;
    }

    setUploadingInvoice(true);
    try {
      const token = localStorage.getItem("authToken");
      
      // Step 1: Get signed upload URL for customer invoice
      const urlResponse = await axios.post(
        `${API_CONST.INVOICE_UPLOAD_REQUEST_URL}/${selectedInvoiceRecord.id}`,
        { fileName: uploadedInvoiceFile.name, invoiceType: 'customer' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { uploadUrl, key } = urlResponse.data;
      
      // Step 2: Upload file directly to signed URL
      await fetch(uploadUrl, {
        method: "PUT",
        body: uploadedInvoiceFile,
        headers: { "Content-Type": uploadedInvoiceFile.type || "application/pdf" },
      });
      
      // Step 3: Submit customer invoice with file path, invoice number and notes
      await axios.post(
        `${API_CONST.SUBMIT_CUSTOMER_INVOICE}/${selectedInvoiceRecord.id}`,
        {
          customerInvoiceFileUrl: key,
          customerInvoiceNumber: invoiceNumber,
          customerNotes: invoiceNotes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updated = receivables.map((r) =>
        r.id === selectedInvoiceRecord.id 
          ? { ...r, invoiceStatus: "Sent", customerInvoiceId: invoiceNumber } 
          : r
      );
      setReceivables(updated);
      
      // Refresh both payables and receivables to reflect changes
      fetchPayables();
      fetchReceivables();
      
      message.success(`Invoice ${invoiceNumber} uploaded and sent to ${selectedInvoiceRecord.customer}`);
      handleCloseInvoiceModal();
    } catch (error) {
      console.error("Error submitting customer invoice:", error);
      message.error(error.response?.data?.message || "Failed to submit customer invoice");
    } finally {
      setUploadingInvoice(false);
    }
  };

  // Action handlers for Partner Payables
  const handleViewPayable = (record) => {
    Modal.info({
      title: `Payable Details - ${record.partnerInvoiceId || record.recordId}`,
      width: 600,
      content: (
        <Descriptions bordered column={1} size="small" style={{ marginTop: 16 }}>
          <Descriptions.Item label="Record ID">{record.recordId}</Descriptions.Item>
          <Descriptions.Item label="Partner Invoice #">{record.partnerInvoiceId || '-'}</Descriptions.Item>
          <Descriptions.Item label="Talent ID">{record.talentId}</Descriptions.Item>
          <Descriptions.Item label="Talent Name">{record.talentName}</Descriptions.Item>
          <Descriptions.Item label="Role">{record.role || '-'}</Descriptions.Item>
          <Descriptions.Item label="Partner">{record.partner}</Descriptions.Item>
          <Descriptions.Item label="Job ID">{record.jobId}</Descriptions.Item>
          <Descriptions.Item label="Month">
            {record.month} {record.year}
          </Descriptions.Item>
          <Descriptions.Item label="Days">{record.days}</Descriptions.Item>
          <Descriptions.Item label="Hours">{record.hours}</Descriptions.Item>
          <Descriptions.Item label="Amount">
            ₹ {(record.partnerAmount || 0).toLocaleString("en-IN")}
          </Descriptions.Item>
          <Descriptions.Item label="GST (18%)">
            ₹ {(record.partnerGst || 0).toLocaleString("en-IN")}
          </Descriptions.Item>
          <Descriptions.Item label="Total Amount">
            <strong>₹ {(record.partnerTotal || 0).toLocaleString("en-IN")}</strong>
          </Descriptions.Item>
          <Descriptions.Item label="Submitted On">{record.submittedOn || '-'}</Descriptions.Item>
          {record.approvedOn && (
            <Descriptions.Item label="Approved On">{record.approvedOn}</Descriptions.Item>
          )}
          {record.paidOn && (
            <Descriptions.Item label="Paid On">{record.paidOn}</Descriptions.Item>
          )}
          <Descriptions.Item label="Partner Status">
            <Tag color={
              record.partnerStatus === "Paid" || record.partnerStatus === "Approved" ? "green"
              : record.partnerStatus === "Verified" ? "blue"
              : record.partnerStatus === "Submitted" ? "cyan"
              : record.partnerStatus === "Overdue" || record.partnerStatus === "Rejected" ? "red"
              : "orange"
            }>
              {record.partnerStatus}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Notes">{record.partnerNotes || '-'}</Descriptions.Item>
        </Descriptions>
      ),
    });
  };

  const handleDownloadInvoice = async (record, invoiceType = 'partner') => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_CONST.INVOICE_DOWNLOAD_URL}/${record.recordId || record.id}?invoiceType=${invoiceType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data?.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      } else {
        message.warning("Invoice file not available");
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
      message.error(error.response?.data?.message || "Failed to download invoice");
    }
  };

  const handleDownloadTimesheetPayable = async (record) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_TIMESHEET_DOWNLOAD_URL,
        { invoiceId: record.id || record.recordId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data?.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      } else {
        message.warning("Timesheet file not available");
      }
    } catch (error) {
      console.error("Error downloading timesheet:", error);
      message.error(error.response?.data?.message || "Failed to download timesheet");
    }
  };

  const handleVerifyPayable = (record) => {
    Modal.confirm({
      title: "Verify Payable",
      content: `Verify invoice ${record.partnerInvoiceId || record.id} from ${record.partner}? This will make it available for approval.`,
      okText: "Verify",
      okButtonProps: { style: { background: "#00d9a9", borderColor: "#00d9a9" } },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.put(
            `${API_CONST.UPDATE_INVOICE_STATUS}/${record.id}`,
            { statusType: "partner", status: "verified" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success(`Invoice verified for ${record.partner}`);
          fetchPayables();
        } catch (error) {
          console.error("Error verifying invoice:", error);
          message.error(error.response?.data?.message || "Failed to verify invoice");
        }
      },
    });
  };

  const handleApprovePayable = (record) => {
    if (record.partnerStatus !== "Verified") {
      message.error("Only verified invoices can be approved. Please verify the invoice first.");
      return;
    }
    
    Modal.confirm({
      title: "Approve Payable",
      content: `Approve payment for ${record.partner} - ${record.partnerInvoiceId || record.id}?`,
      okText: "Approve",
      okButtonProps: { style: { background: "#00d9a9", borderColor: "#00d9a9" } },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.put(
            `${API_CONST.UPDATE_INVOICE_STATUS}/${record.id}`,
            { statusType: "partner", status: "approved" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success(`Payment approved for ${record.partner}`);
          fetchPayables();
        } catch (error) {
          console.error("Error approving invoice:", error);
          message.error(error.response?.data?.message || "Failed to approve invoice");
        }
      },
    });
  };

  const handleModifyPayable = (record) => {
    setSelectedPayableRecord(record);
    setPartnerInvoiceModalVisible(true);
    setPartnerInvoiceNumber(record.partnerInvoiceId || "");
    setPartnerInvoiceNotes(record.partnerNotes || "");
  };

  const handleClosePartnerInvoiceModal = () => {
    setPartnerInvoiceModalVisible(false);
    setSelectedPayableRecord(null);
    setPartnerInvoiceNumber("");
    setPartnerInvoiceNotes("");
    setUploadedPartnerInvoiceFile(null);
  };

  const handleSubmitPartnerInvoice = async () => {
    if (!uploadedPartnerInvoiceFile && !selectedPayableRecord?.partnerInvoiceId) {
      message.warning("Please upload an invoice file");
      return;
    }
    if (!partnerInvoiceNumber.trim()) {
      message.warning("Please enter an invoice number");
      return;
    }

    setUploadingInvoice(true);
    try {
      const token = localStorage.getItem("authToken");
      let key = selectedPayableRecord?.partnerInvoicePath;
      
      if (uploadedPartnerInvoiceFile) {
        const urlResponse = await axios.post(
          `${API_CONST.INVOICE_UPLOAD_REQUEST_URL}/${selectedPayableRecord.id}`,
          { fileName: uploadedPartnerInvoiceFile.name, invoiceType: 'partner' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const { uploadUrl, key: newKey } = urlResponse.data;
        key = newKey;
        
        await fetch(uploadUrl, {
          method: "PUT",
          body: uploadedPartnerInvoiceFile,
          headers: { "Content-Type": uploadedPartnerInvoiceFile.type || "application/pdf" },
        });
      }
      
      await axios.post(
        `${API_CONST.SUBMIT_PARTNER_INVOICE}/${selectedPayableRecord.id}`,
        {
          partnerInvoiceNumber: partnerInvoiceNumber,
          partnerInvoiceFileUrl: key,
          partnerNotes: partnerInvoiceNotes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      message.success("Partner invoice updated successfully");
      handleClosePartnerInvoiceModal();
      fetchPayables();
    } catch (error) {
      console.error("Error submitting partner invoice:", error);
      message.error(error.response?.data?.message || "Failed to submit partner invoice");
    } finally {
      setUploadingInvoice(false);
    }
  };

  const handleRejectPayable = (record) => {
    Modal.confirm({
      title: "Reject Payable",
      content: `Reject payment for ${record.partner} - ${record.partnerInvoiceId || record.id}?`,
      okText: "Reject",
      okType: "danger",
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.put(
            `${API_CONST.UPDATE_INVOICE_STATUS}/${record.id}`,
            { statusType: "partner", status: "rejected" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.warning(`Payment rejected for ${record.partner}`);
          fetchPayables();
        } catch (error) {
          console.error("Error rejecting invoice:", error);
          message.error(error.response?.data?.message || "Failed to reject invoice");
        }
      },
    });
  };

  // Action handlers for Invoice Reconciliation
  const handleViewReconciliationBreakup = (record) => {
    Modal.info({
      title: `Reconciliation Details - ${record.talentName}`,
      width: 700,
      content: (
        <Descriptions bordered column={1} size="small" style={{ marginTop: 16 }}>
          <Descriptions.Item label="Talent ID">{record.talentId}</Descriptions.Item>
          <Descriptions.Item label="Talent Name">{record.talentName}</Descriptions.Item>
          <Descriptions.Item label="Partner">{record.partner}</Descriptions.Item>
          <Descriptions.Item label="Customer">{record.customer}</Descriptions.Item>
          <Descriptions.Item label="Job ID">{record.jobId}</Descriptions.Item>
          <Descriptions.Item label="Record ID">{record.recordId}</Descriptions.Item>
          <Descriptions.Item label="Partner Invoice ID">
            {record.partnerInvoiceId}
          </Descriptions.Item>
          <Descriptions.Item label="Customer Invoice ID">
            {record.customerInvoiceId}
          </Descriptions.Item>
          <Descriptions.Item label="Month">
            {record.month} {record.year}
          </Descriptions.Item>
          <Descriptions.Item label="Receivable Status">
            <Tag
              color={
                record.receivableStatus === "Paid"
                  ? "green"
                  : record.receivableStatus === "Sent"
                  ? "blue"
                  : "orange"
              }
            >
              {record.receivableStatus}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Payable Status">
            <Tag
              color={
                record.payableStatus === "Paid"
                  ? "green"
                  : record.payableStatus === "Submitted"
                  ? "orange"
                  : "default"
              }
            >
              {record.payableStatus}
            </Tag>
          </Descriptions.Item>
        </Descriptions>
      ),
    });
  };

  const handleModifyFee = (record) => {
    setSelectedFeeRecord(record);
    // Set default percentages from the record (will come from Admin Settings in future)
    setClientFeePercentage(record.clientFeePercentage || 10);
    setPartnerFeePercentage(record.partnerFeePercentage || 5);
    setModifyFeeModalVisible(true);
  };

  const handleCloseModifyFeeModal = () => {
    setModifyFeeModalVisible(false);
    setSelectedFeeRecord(null);
    setClientFeePercentage(10);
    setPartnerFeePercentage(5);
  };

  const handleSubmitModifyFee = () => {
    if (clientFeePercentage < 0 || partnerFeePercentage < 0) {
      message.warning("Fee percentages cannot be negative");
      return;
    }

    // Update the reconciliation record with new fee percentages
    const updated = reconciliation.map((r) =>
      r.id === selectedFeeRecord.id
        ? { 
            ...r, 
            clientFeePercentage, 
            partnerFeePercentage,
            feeModifiedOn: new Date().toISOString().split("T")[0] 
          }
        : r
    );
    setReconciliation(updated);
    
    const clientAmount = selectedFeeRecord.baseCost * (1 + clientFeePercentage / 100);
    const partnerAmount = selectedFeeRecord.baseCost * (1 - partnerFeePercentage / 100);
    const revenue = clientAmount - partnerAmount;
    
    message.success(
      `Fee modified for ${selectedFeeRecord.talentName}: Client ${clientFeePercentage}%, Partner ${partnerFeePercentage}% | Revenue: ₹${revenue.toLocaleString("en-IN")}`
    );
    handleCloseModifyFeeModal();
  };

  const handleUpdateReceivable = (record) => {
    Modal.confirm({
      title: "Update Receivable Status",
      content: `Mark receivable as paid for ${record.talentName}? This will update the customer invoice status to Paid.`,
      okText: "Mark as Paid",
      okButtonProps: { style: { background: "#00d9a9", borderColor: "#00d9a9" } },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.put(`${API_CONST.UPDATE_INVOICE_STATUS}/${record.id}`, {
            statusType: 'customer',
            status: 'paid'
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const updated = reconciliation.map((r) =>
            r.id === record.id ? { ...r, receivableStatus: "Paid" } : r
          );
          setReconciliation(updated);
          message.success(`Receivable marked as paid for ${record.talentName}`);
          fetchPayables();
        } catch (error) {
          console.error("Error updating receivable:", error);
          message.error(error.response?.data?.message || "Failed to update receivable status");
        }
      },
    });
  };

  const handleUpdatePayable = (record) => {
    Modal.confirm({
      title: "Update Payable Status",
      content: `Mark payable as paid for ${record.talentName}? This will update the partner invoice status to Paid.`,
      okText: "Mark as Paid",
      okButtonProps: { style: { background: "#00d9a9", borderColor: "#00d9a9" } },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.put(`${API_CONST.UPDATE_INVOICE_STATUS}/${record.id}`, {
            statusType: 'partner',
            status: 'paid'
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const updated = reconciliation.map((r) =>
            r.id === record.id ? { ...r, payableStatus: "Paid" } : r
          );
          setReconciliation(updated);
          message.success(`Payable marked as paid for ${record.talentName}`);
          fetchPayables();
        } catch (error) {
          console.error("Error updating payable:", error);
          message.error(error.response?.data?.message || "Failed to update payable status");
        }
      },
    });
  };

  // Action handlers for Revenue Analysis
  const handleViewTransaction = (record) => {
    Modal.info({
      title: `Transaction Details - ${record.talentName}`,
      width: 700,
      content: (
        <Descriptions bordered column={1} size="small" style={{ marginTop: 16 }}>
          <Descriptions.Item label="Talent ID">{record.talentId}</Descriptions.Item>
          <Descriptions.Item label="Talent Name">{record.talentName}</Descriptions.Item>
          <Descriptions.Item label="Partner">{record.partner}</Descriptions.Item>
          <Descriptions.Item label="Customer">{record.customer}</Descriptions.Item>
          <Descriptions.Item label="Job ID">{record.jobId}</Descriptions.Item>
          <Descriptions.Item label="Month">
            {record.month} {record.year}
          </Descriptions.Item>
          <Descriptions.Item label="Talent Cost (Base)">
            ₹ {record.talentCost.toLocaleString("en-IN")}
          </Descriptions.Item>
          <Descriptions.Item label="Amount Receivable (10% markup)">
            ₹ {record.amountReceivable.toLocaleString("en-IN")}
          </Descriptions.Item>
          <Descriptions.Item label="GST IN (18%)">
            ₹ {record.gstIn.toLocaleString("en-IN")}
          </Descriptions.Item>
          <Descriptions.Item label="Total Receivable">
            <strong>
              ₹ {(record.amountReceivable + record.gstIn).toLocaleString("en-IN")}
            </strong>
          </Descriptions.Item>
          <Descriptions.Item label="Amount Payable (5% deduction)">
            ₹ {record.amountPayable.toLocaleString("en-IN")}
          </Descriptions.Item>
          <Descriptions.Item label="GST OUT (18%)">
            ₹ {record.gstOut.toLocaleString("en-IN")}
          </Descriptions.Item>
          <Descriptions.Item label="Total Payable">
            <strong>
              ₹ {(record.amountPayable + record.gstOut).toLocaleString("en-IN")}
            </strong>
          </Descriptions.Item>
          <Descriptions.Item label="Net Revenue">
            <strong style={{ color: "#52c41a", fontSize: "16px" }}>
              ₹ {record.revenue.toLocaleString("en-IN")}
            </strong>
          </Descriptions.Item>
          <Descriptions.Item label="Profit Margin">
            {((record.revenue / record.amountReceivable) * 100).toFixed(2)}%
          </Descriptions.Item>
        </Descriptions>
      ),
    });
  };

  const handleModifyRecord = (record) => {
    message.info(`Modify record for ${record.talentName} - Implementation pending`);
  };

  // Columns for Client Billing
  const receivableColumns = [
    {
      title: "Record ID",
      dataIndex: "recordId",
      key: "recordId",
      width: 110,
      ellipsis: true,
      render: (text) => <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{text?.substring(0, 12)}...</span>,
    },
    {
      title: "Partner Inv Ref",
      dataIndex: "partnerInvoiceId",
      key: "partnerInvoiceId",
      width: 140,
      ellipsis: true,
      render: (text) => <span style={{ fontFamily: 'monospace', color: '#1890ff', fontSize: 12, fontWeight: 500 }}>{text || '-'}</span>,
    },
    {
      title: "Client Invoice",
      dataIndex: "customerInvoiceId",
      key: "customerInvoiceId",
      width: 150,
      render: (text) => <span style={{ fontFamily: 'monospace', color: '#52c41a', fontWeight: 500 }}>{text || '-'}</span>,
    },
    {
      title: "Talent Name",
      dataIndex: "talentName",
      key: "talentName",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      width: 140,
      ellipsis: true,
      render: (org) => org ? (
        <a
          style={{ color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/admin/organization/${encodeURIComponent(org)}`, {
            state: { from: "/admin/finance", fromLabel: "Back to Finance" }
          })}
        >
          {org}
        </a>
      ) : "-",
    },
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      width: 85,
      render: (text, record) => `${text?.substring(0, 3)} '${record.year?.toString().substring(2)}`,
    },
    {
      title: "Days",
      dataIndex: "days",
      key: "days",
      width: 70,
      align: /** @type {'center'} */ ("center"),
    },
    {
      title: "Hrs",
      dataIndex: "hours",
      key: "hours",
      width: 75,
      align: /** @type {'center'} */ ("center"),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      align: /** @type {'right'} */ ("right"),
      render: (amount) => (
        <span style={{ fontWeight: 600, color: "#595959" }}>
          ₹ {(amount || 0).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "GST (18%)",
      dataIndex: "gst",
      key: "gst",
      width: 110,
      align: /** @type {'right'} */ ("right"),
      render: (gst) => (
        <span style={{ fontWeight: 500, color: "#8c8c8c" }}>
          ₹ {(gst || 0).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "Total Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 140,
      align: /** @type {'right'} */ ("right"),
      render: (total) => (
        <strong style={{ color: "#014c75", fontSize: 14 }}>
          ₹ {(total || 0).toLocaleString("en-IN")}
        </strong>
      ),
    },
    {
      title: "Timesheet",
      dataIndex: "timesheetStatus",
      key: "timesheetStatus",
      width: 110,
      align: /** @type {'center'} */ ("center"),
      render: (status) => (
        <Tag color={status === "Approved" ? "green" : status === "Verified" ? "blue" : "orange"}>{status}</Tag>
      ),
    },
    {
      title: "Invoice",
      dataIndex: "invoiceStatus",
      key: "invoiceStatus",
      width: 90,
      render: (status) => (
        <Tag color={
          status === "Paid" || status === "Approved" ? "green"
          : status === "Sent" ? "blue"
          : status === "Overdue" || status === "Rejected" ? "red"
          : "orange"
        }>{status}</Tag>
      ),
    },
    {
      title: "Notes",
      dataIndex: "customerNotes",
      key: "customerNotes",
      width: 140,
      ellipsis: true,
      render: (text) => text || "-",
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => {
        const menuItems = [
          {
            key: "viewBreakup",
            label: "View Details",
            icon: <EyeOutlined />,
            onClick: () => handleViewBreakup(record),
          },
          {
            key: "downloadTimesheet",
            label: "Download Timesheet",
            icon: <DownloadOutlined />,
            onClick: () => handleDownloadTimesheet(record),
          },
        ];

        if (record.invoiceStatus === "Draft" || record.invoiceStatus === "Pending") {
          menuItems.unshift({
            key: "uploadInvoice",
            label: "Upload Invoice",
            icon: <SendOutlined />,
            onClick: () => handleCreateInvoice(record),
          });
        }

        if (record.invoiceStatus === "Rejected") {
          menuItems.unshift({
            key: "modifyAndReissue",
            label: "Modify & Re-issue",
            icon: <EditOutlined />,
            onClick: () => handleCreateInvoice(record),
          });
        }

        if (record.invoiceStatus === "Sent" || record.invoiceStatus === "Paid" || record.invoiceStatus === "Approved") {
          menuItems.push({
            key: "downloadInvoice",
            label: "Download Invoice",
            icon: <DownloadOutlined />,
            onClick: () => handleDownloadInvoice(record, 'customer'),
          });
          menuItems.push({
            key: "editInvoice",
            label: "Edit Invoice",
            icon: <EditOutlined />,
            onClick: () => handleCreateInvoice(record),
          });
        }

        if (record.invoiceStatus === "Sent") {
          menuItems.push({
            key: "approveInvoice",
            label: "Approve Invoice",
            icon: <CheckCircleOutlined />,
            onClick: () => handleApproveReceivable(record),
          });
        }

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
          >
            <MoreOutlined style={{ cursor: "pointer", fontSize: "18px" }} />
          </Dropdown>
        );
      },
    },
  ];

  // Columns for Partner Payables
  const payableColumns = [
    {
      title: "Record ID",
      dataIndex: "recordId",
      key: "recordId",
      width: 110,
      ellipsis: true,
      render: (text) => <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{text?.substring(0, 12)}...</span>,
    },
    {
      title: "Partner Invoice",
      dataIndex: "partnerInvoiceId",
      key: "partnerInvoiceId",
      width: 150,
      ellipsis: true,
      render: (text) => <span style={{ fontFamily: 'monospace', color: '#1890ff', fontWeight: 500 }}>{text || '-'}</span>,
    },
    {
      title: "Talent Name",
      dataIndex: "talentName",
      key: "talentName",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 130,
      ellipsis: true,
    },
    {
      title: "Partner",
      dataIndex: "partner",
      key: "partner",
      width: 140,
      ellipsis: true,
      render: (org) => org ? (
        <a
          style={{ color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/admin/organization/${encodeURIComponent(org)}`, {
            state: { from: "/admin/finance", fromLabel: "Back to Finance" }
          })}
        >
          {org}
        </a>
      ) : "-",
    },
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      width: 85,
      render: (text, record) => `${text?.substring(0, 3)} '${record.year?.toString().substring(2)}`,
    },
    {
      title: "Days",
      dataIndex: "days",
      key: "days",
      width: 70,
      align: /** @type {'center'} */ ("center"),
    },
    {
      title: "Hrs",
      dataIndex: "hours",
      key: "hours",
      width: 75,
      align: /** @type {'center'} */ ("center"),
    },
    {
      title: "Amount",
      dataIndex: "partnerAmount",
      key: "partnerAmount",
      width: 120,
      align: /** @type {'right'} */ ("right"),
      render: (amount) => (
        <span style={{ fontWeight: 600, color: "#595959" }}>
          ₹ {(amount || 0).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "GST (18%)",
      dataIndex: "partnerGst",
      key: "partnerGst",
      width: 110,
      align: /** @type {'right'} */ ("right"),
      render: (gst) => (
        <span style={{ fontWeight: 500, color: "#8c8c8c" }}>
          ₹ {(gst || 0).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "Total Amount",
      dataIndex: "partnerTotal",
      key: "partnerTotal",
      width: 140,
      align: /** @type {'right'} */ ("right"),
      render: (total) => (
        <strong style={{ color: "#014c75", fontSize: 14 }}>
          ₹ {(total || 0).toLocaleString("en-IN")}
        </strong>
      ),
    },
    {
      title: "Timesheet",
      dataIndex: "timesheetStatus",
      key: "timesheetStatus",
      width: 110,
      align: /** @type {'center'} */ ("center"),
      render: (status) => (
        <Tag color={status === "Approved" ? "green" : status === "Verified" ? "blue" : "orange"}>{status}</Tag>
      ),
    },
    {
      title: "Invoice",
      dataIndex: "partnerStatus",
      key: "partnerStatus",
      width: 100,
      render: (status) => (
        <Tag
          color={
            status === "Paid"
              ? "green"
              : status === "Approved"
              ? "cyan"
              : status === "Verified"
              ? "blue"
              : status === "Submitted"
              ? "purple"
              : status === "Overdue" || status === "Rejected"
              ? "red"
              : "orange"
          }
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Notes",
      dataIndex: "partnerNotes",
      key: "partnerNotes",
      width: 140,
      ellipsis: true,
      render: (text) => text || "-",
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => {
        const menuItems = [
          {
            key: "view",
            label: "View",
            icon: <EyeOutlined />,
            onClick: () => handleViewPayable(record),
          },
          {
            key: "downloadInvoice",
            label: "Download Invoice",
            icon: <DownloadOutlined />,
            onClick: () => handleDownloadInvoice(record),
          },
          {
            key: "downloadTimesheet",
            label: "Download Timesheet",
            icon: <FileTextOutlined />,
            onClick: () => handleDownloadTimesheetPayable(record),
          },
        ];

        // Only show Verify for Submitted status
        if (record.partnerStatus === "Submitted") {
          menuItems.push({
            key: "verify",
            label: "Verify",
            icon: <CheckCircleOutlined />,
            onClick: () => handleVerifyPayable(record),
          });
        }

        // Only show Approve for Verified status
        if (record.partnerStatus === "Verified") {
          menuItems.push({
            key: "approve",
            label: "Approve",
            icon: <CheckOutlined />,
            onClick: () => handleApprovePayable(record),
          });
        }

        menuItems.push({
          key: "modify",
          label: "Modify",
          icon: <EditOutlined />,
          onClick: () => handleModifyPayable(record),
        });

        // Only show Reject for Submitted status
        if (record.partnerStatus === "Submitted") {
          menuItems.push({
            key: "reject",
            label: "Reject",
            icon: <CloseOutlined />,
            onClick: () => handleRejectPayable(record),
            danger: true,
          });
        }

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
          >
            <MoreOutlined style={{ cursor: "pointer", fontSize: "18px" }} />
          </Dropdown>
        );
      },
    },
  ];

  // Columns for Invoice Reconciliation
  const reconciliationColumns = [
    {
      title: "Talent ID",
      dataIndex: "talentId",
      key: "talentId",
      width: 90,
    },
    {
      title: "Talent Name",
      dataIndex: "talentName",
      key: "talentName",
      width: 130,
      ellipsis: true,
    },
    {
      title: "Partner",
      dataIndex: "partner",
      key: "partner",
      width: 130,
      ellipsis: true,
      render: (org) => org ? (
        <a
          style={{ color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/admin/organization/${encodeURIComponent(org)}`, {
            state: { from: "/admin/finance", fromLabel: "Back to Finance" }
          })}
        >
          {org}
        </a>
      ) : "-",
    },
    {
      title: "Record ID",
      dataIndex: "recordId",
      key: "recordId",
      width: 110,
      ellipsis: true,
    },
    {
      title: "Partner Invoice ID",
      dataIndex: "partnerInvoiceId",
      key: "partnerInvoiceId",
      width: 130,
      ellipsis: true,
    },
    {
      title: "Job ID",
      dataIndex: "jobId",
      key: "jobId",
      width: 100,
      ellipsis: true,
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      width: 130,
      ellipsis: true,
      render: (org) => org ? (
        <a
          style={{ color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/admin/organization/${encodeURIComponent(org)}`, {
            state: { from: "/admin/finance", fromLabel: "Back to Finance" }
          })}
        >
          {org}
        </a>
      ) : "-",
    },
    {
      title: "Customer Invoice ID",
      dataIndex: "customerInvoiceId",
      key: "customerInvoiceId",
      width: 130,
      ellipsis: true,
    },
    {
      title: "Receivable",
      dataIndex: "receivableStatus",
      key: "receivableStatus",
      width: 100,
      render: (status) => (
        <Tag
          color={
            status === "Paid" ? "green"
            : status === "Approved" ? "cyan"
            : status === "Sent" ? "blue"
            : status === "Overdue" || status === "Rejected" ? "red"
            : "orange"
          }
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Payable",
      dataIndex: "payableStatus",
      key: "payableStatus",
      width: 100,
      render: (status) => (
        <Tag
          color={
            status === "Paid" ? "green"
            : status === "Approved" ? "cyan"
            : status === "Verified" ? "blue"
            : status === "Submitted" ? "purple"
            : status === "Overdue" || status === "Rejected" ? "red"
            : "orange"
          }
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "viewBreakup",
                label: "View Breakup",
                icon: <EyeOutlined />,
                onClick: () => handleViewReconciliationBreakup(record),
              },
              {
                key: "modifyFee",
                label: "Modify Fee",
                icon: <EditOutlined />,
                onClick: () => handleModifyFee(record),
              },
              {
                key: "updateReceivable",
                label: "Update Receivable",
                icon: <SyncOutlined />,
                onClick: () => handleUpdateReceivable(record),
                disabled: record.receivableStatus !== "Approved",
              },
              {
                key: "updatePayable",
                label: "Update Payable",
                icon: <SyncOutlined />,
                onClick: () => handleUpdatePayable(record),
                disabled: record.payableStatus === "Paid",
              },
            ],
          }}
          trigger={["click"]}
        >
          <MoreOutlined style={{ cursor: "pointer", fontSize: "18px" }} />
        </Dropdown>
      ),
    },
  ];

  // Columns for Revenue Analysis
  const revenueAnalysisColumns = [
    {
      title: "Talent ID",
      dataIndex: "talentId",
      key: "talentId",
      width: 90,
    },
    {
      title: "Talent Name",
      dataIndex: "talentName",
      key: "talentName",
      width: 130,
      ellipsis: true,
    },
    {
      title: "Partner",
      dataIndex: "partner",
      key: "partner",
      width: 130,
      ellipsis: true,
      render: (org) => org ? (
        <a
          style={{ color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/admin/organization/${encodeURIComponent(org)}`, {
            state: { from: "/admin/finance", fromLabel: "Back to Finance" }
          })}
        >
          {org}
        </a>
      ) : "-",
    },
    {
      title: "Job ID",
      dataIndex: "jobId",
      key: "jobId",
      width: 100,
      ellipsis: true,
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      width: 130,
      ellipsis: true,
      render: (org) => org ? (
        <a
          style={{ color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/admin/organization/${encodeURIComponent(org)}`, {
            state: { from: "/admin/finance", fromLabel: "Back to Finance" }
          })}
        >
          {org}
        </a>
      ) : "-",
    },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
      width: 110,
      ellipsis: true,
      render: (revenue) => (
        <span style={{ fontWeight: 700, color: "#52c41a" }}>
          ₹ {revenue.toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "Receivable",
      dataIndex: "amountReceivable",
      key: "amountReceivable",
      width: 110,
      ellipsis: true,
      render: (amount) => `₹ ${amount.toLocaleString("en-IN")}`,
    },
    {
      title: "GST IN",
      dataIndex: "gstIn",
      key: "gstIn",
      width: 90,
      ellipsis: true,
      render: (gst) => `₹ ${gst.toLocaleString("en-IN")}`,
    },
    {
      title: "Payable",
      dataIndex: "amountPayable",
      key: "amountPayable",
      width: 110,
      ellipsis: true,
      render: (amount) => `₹ ${amount.toLocaleString("en-IN")}`,
    },
    {
      title: "GST OUT",
      dataIndex: "gstOut",
      key: "gstOut",
      width: 90,
      ellipsis: true,
      render: (gst) => `₹ ${gst.toLocaleString("en-IN")}`,
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "viewTransaction",
                label: "View Transaction",
                icon: <LineChartOutlined />,
                onClick: () => handleViewTransaction(record),
              },
            ],
          }}
          trigger={["click"]}
        >
          <MoreOutlined style={{ cursor: "pointer", fontSize: "18px" }} />
        </Dropdown>
      ),
    },
  ];

  // Render metrics based on active tab
  const renderMetrics = () => {
    if (activeTab === "Client Billing") {
      const metrics = getReceivableMetrics();
      return (
        <MetricsSection>
          <MetricCard>
            <div className="metric-label">Total Amount Received</div>
            <div className="metric-value">
              ₹ {(metrics.totalAmount / 100000).toFixed(2)}L
            </div>
          </MetricCard>
          <MetricCard>
            <div className="metric-label">Current Receivable</div>
            <div className="metric-value">
              ₹ {(metrics.currentReceivable / 100000).toFixed(2)}L
            </div>
          </MetricCard>
          <MetricCard>
            <div className="metric-label">Overdue Amount</div>
            <div className="metric-value" style={{ color: "#ff4d4f" }}>
              ₹ {(metrics.overdueAmount / 100000).toFixed(2)}L
            </div>
          </MetricCard>
          <MetricCard>
            <div className="metric-label">Total Invoices</div>
            <div className="metric-value">{metrics.totalInvoices}</div>
          </MetricCard>
        </MetricsSection>
      );
    }

    if (activeTab === "Partner Payables") {
      const metrics = getPayableMetrics();
      return (
        <MetricsSection>
          <MetricCard>
            <div className="metric-label">Total Amount Paid</div>
            <div className="metric-value">
              ₹ {(metrics.totalPaid / 100000).toFixed(2)}L
            </div>
          </MetricCard>
          <MetricCard>
            <div className="metric-label">Current Payable</div>
            <div className="metric-value">
              ₹ {(metrics.currentPayable / 100000).toFixed(2)}L
            </div>
          </MetricCard>
          <MetricCard>
            <div className="metric-label">Overdue Payable</div>
            <div className="metric-value" style={{ color: "#ff4d4f" }}>
              ₹ {(metrics.overduePayable / 100000).toFixed(2)}L
            </div>
          </MetricCard>
          <MetricCard>
            <div className="metric-label">Total Invoices</div>
            <div className="metric-value">{metrics.totalInvoices}</div>
          </MetricCard>
        </MetricsSection>
      );
    }

    if (activeTab === "Invoice Reconciliation") {
      const metrics = getReconciliationMetrics();
      return (
        <MetricsSection>
          <MetricCard>
            <div className="metric-label">Total Invoice Paid</div>
            <div className="metric-value">{metrics.totalInvoicePaid}</div>
          </MetricCard>
          <MetricCard>
            <div className="metric-label">Total Bills Paid</div>
            <div className="metric-value">{metrics.totalBillsPaid}</div>
          </MetricCard>
          <MetricCard>
            <div className="metric-label">Receivable Invoice Pending</div>
            <div className="metric-value" style={{ color: "#ff4d4f" }}>
              {metrics.receivablePending}
            </div>
          </MetricCard>
          <MetricCard>
            <div className="metric-label">Payable Pending</div>
            <div className="metric-value" style={{ color: "#ff4d4f" }}>
              {metrics.payablePending}
            </div>
          </MetricCard>
        </MetricsSection>
      );
    }

    if (activeTab === "Revenue Analysis") {
      const metrics = getRevenueMetrics();
      return (
        <MetricsSection>
          <MetricCard>
            <div className="metric-label">Total Revenue</div>
            <div className="metric-value" style={{ color: "#52c41a" }}>
              ₹ {((metrics.totalRevenue || 0) / 100000).toFixed(2)}L
            </div>
          </MetricCard>
          <MetricCard>
            <div className="metric-label">Total Receivable</div>
            <div className="metric-value">
              ₹ {((metrics.totalReceivable || 0) / 100000).toFixed(2)}L
            </div>
          </MetricCard>
          <MetricCard>
            <div className="metric-label">Total Payable</div>
            <div className="metric-value">
              ₹ {((metrics.totalPayable || 0) / 100000).toFixed(2)}L
            </div>
          </MetricCard>
          <MetricCard>
            <div className="metric-label">GST (In / Out)</div>
            <div className="metric-value">
              ₹ {((metrics.totalGSTIn || 0) / 100000).toFixed(2)}L / ₹ {((metrics.totalGSTOut || 0) / 100000).toFixed(2)}L
            </div>
          </MetricCard>
        </MetricsSection>
      );
    }

    if (activeTab === "Financial Insights") {
      const summary = financialInsights.summary || {};
      const netCashFlow = summary.netCashFlow || 0;

      return (
        <MetricsSection>
          <MetricCard>
            <div className="metric-label">Total Receivable</div>
            <div className="metric-value">
              ₹ {((summary.totalReceivable || 0) / 100000).toFixed(2)}L
            </div>
          </MetricCard>
          <MetricCard>
            <div className="metric-label">Total Payable</div>
            <div className="metric-value">
              ₹ {((summary.totalPayable || 0) / 100000).toFixed(2)}L
            </div>
          </MetricCard>
          <MetricCard>
            <div className="metric-label">Net Cash Flow</div>
            <div
              className="metric-value"
              style={{ color: netCashFlow >= 0 ? "#52c41a" : "#ff4d4f" }}
            >
              ₹ {(netCashFlow / 100000).toFixed(2)}L
            </div>
          </MetricCard>
          <MetricCard>
            <div className="metric-label">Total Net Revenue</div>
            <div className="metric-value" style={{ color: "#52c41a" }}>
              ₹ {((summary.totalRevenue || 0) / 100000).toFixed(2)}L
            </div>
          </MetricCard>
        </MetricsSection>
      );
    }

    return null;
  };

  // Render filters based on active tab
  const renderFilters = () => {
    // Financial Insights has different filters
    if (activeTab === "Financial Insights") {
      return (
        <Flex align="start" justify="space-between" style={{ marginBottom: "20px" }}>
          <Flex gap="middle">
            <Select
              placeholder={
                <span>
                  <FilterOutlined style={{ marginRight: 8 }} />
                  Filter by Period
                </span>
              }
              value={filterPeriod || undefined}
              onChange={setFilterPeriod}
              allowClear
              style={{ width: 180 }}
            >
              <Option value="Monthly">Monthly</Option>
              <Option value="Quarterly">Quarterly</Option>
              <Option value="Yearly">Yearly</Option>
            </Select>
            <Select
              placeholder={
                <span>
                  <FilterOutlined style={{ marginRight: 8 }} />
                  Filter by Year
                </span>
              }
              value={filterYear || undefined}
              onChange={setFilterYear}
              allowClear
              style={{ width: 150 }}
            >
              <Option value="2024">2024</Option>
              <Option value="2023">2023</Option>
              <Option value="2022">2022</Option>
            </Select>
          </Flex>
        </Flex>
      );
    }

    return (
      <Flex align="start" justify="space-between" style={{ marginBottom: "20px" }}>
        <Flex gap="middle">
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search by name, ID, customer, or partner..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          {activeTab !== "Revenue Analysis" && (
            <Select
              placeholder={
                <span>
                  <FilterOutlined style={{ marginRight: 8 }} />
                  Filter by Partner
                </span>
              }
              value={filterPartner || undefined}
              onChange={setFilterPartner}
              allowClear
              style={{ width: 220 }}
            >
              <Option value="TechCorp Solutions">TechCorp Solutions</Option>
              <Option value="Digital Partners Inc">Digital Partners Inc</Option>
              <Option value="Innovate Tech">Innovate Tech</Option>
              <Option value="CodeCraft Ltd">CodeCraft Ltd</Option>
            </Select>
          )}
          {(activeTab === "Client Billing" ||
            activeTab === "Invoice Reconciliation" ||
            activeTab === "Revenue Analysis") && (
            <Select
              placeholder={
                <span>
                  <FilterOutlined style={{ marginRight: 8 }} />
                  Filter by Customer
                </span>
              }
              value={filterCustomer || undefined}
              onChange={setFilterCustomer}
              allowClear
              style={{ width: 220 }}
            >
              <Option value="Amazon Inc">Amazon Inc</Option>
              <Option value="Google LLC">Google LLC</Option>
              <Option value="Microsoft Corporation">Microsoft Corporation</Option>
              <Option value="Adobe Systems">Adobe Systems</Option>
            </Select>
          )}
          <Select
            placeholder={
              <span>
                <FilterOutlined style={{ marginRight: 8 }} />
                Filter by Month
              </span>
            }
            value={filterMonth || undefined}
            onChange={setFilterMonth}
            allowClear
            style={{ width: 200 }}
          >
            <Option value="January">January</Option>
            <Option value="February">February</Option>
            <Option value="November">November</Option>
            <Option value="December">December</Option>
          </Select>
          <Select
            placeholder={
              <span>
                <FilterOutlined style={{ marginRight: 8 }} />
                Filter by Year
              </span>
            }
            value={filterYear || undefined}
            onChange={setFilterYear}
            allowClear
            style={{ width: 150 }}
          >
            <Option value="2024">2024</Option>
            <Option value="2023">2023</Option>
            <Option value="2022">2022</Option>
          </Select>
          {activeTab !== "Revenue Analysis" && (
            <Select
              placeholder={
                <span>
                  <FilterOutlined style={{ marginRight: 8 }} />
                  Filter by Status
                </span>
              }
              value={filterStatus || undefined}
              onChange={setFilterStatus}
              allowClear
              style={{ width: 180 }}
            >
              {activeTab === "Client Billing" && (
                <>
                  <Option value="Draft">Draft</Option>
                  <Option value="Sent">Sent</Option>
                  <Option value="Approved">Approved</Option>
                  <Option value="Paid">Paid</Option>
                  <Option value="Overdue">Overdue</Option>
                </>
              )}
              {activeTab === "Partner Payables" && (
                <>
                  <Option value="Submitted">Submitted</Option>
                  <Option value="Verified">Verified</Option>
                  <Option value="Approved">Approved</Option>
                  <Option value="Paid">Paid</Option>
                  <Option value="Rejected">Rejected</Option>
                </>
              )}
              {activeTab === "Invoice Reconciliation" && (
                <>
                  <Option value="Pending">Pending</Option>
                  <Option value="Sent">Sent</Option>
                  <Option value="Submitted">Submitted</Option>
                  <Option value="Paid">Paid</Option>
                </>
              )}
            </Select>
          )}
        </Flex>
      </Flex>
    );
  };

  // Render table based on active tab
  const renderTable = () => {
    if (activeTab === "Client Billing") {
      const data = getFilteredReceivables();
      return (
        <TableContainer>
          {data.length > 0 ? (
            <Table
              columns={receivableColumns}
              dataSource={data}
              rowKey="id"
              pagination={{
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
                pageSize: 10,
                showTotal: (total) => `Total ${total} records`,
              }}
              scroll={{ x: 1600 }}
            />
          ) : (
            <EmptyState>
              <Empty description="No billing records found" />
            </EmptyState>
          )}
        </TableContainer>
      );
    }

    if (activeTab === "Partner Payables") {
      const data = getFilteredPayables();
      return (
        <TableContainer>
          {data.length > 0 ? (
            <Table
              columns={payableColumns}
              dataSource={data}
              rowKey="id"
              pagination={{
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
                pageSize: 10,
                showTotal: (total) => `Total ${total} records`,
              }}
              scroll={{ x: 1500 }}
            />
          ) : (
            <EmptyState>
              <Empty description="No payable records found" />
            </EmptyState>
          )}
        </TableContainer>
      );
    }

    if (activeTab === "Invoice Reconciliation") {
      const data = getFilteredReconciliation();
      return (
        <TableContainer>
          {data.length > 0 ? (
            <Table
              columns={reconciliationColumns}
              dataSource={data}
              rowKey="id"
              pagination={{
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
                pageSize: 10,
                showTotal: (total) => `Total ${total} records`,
              }}
              scroll={{ x: 1400 }}
            />
          ) : (
            <EmptyState>
              <Empty description="No reconciliation records found" />
            </EmptyState>
          )}
        </TableContainer>
      );
    }

    if (activeTab === "Revenue Analysis") {
      const data = getFilteredRevenueAnalysis();
      return (
        <TableContainer>
          {data.length > 0 ? (
            <Table
              columns={revenueAnalysisColumns}
              dataSource={data}
              rowKey="id"
              loading={loadingRevenueAnalysis}
              pagination={{
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
                pageSize: 10,
                showTotal: (total) => `Total ${total} records`,
              }}
              scroll={{ x: 1300 }}
            />
          ) : (
            <EmptyState>
              <Empty description={loadingRevenueAnalysis ? "Loading..." : "No revenue records found"} />
            </EmptyState>
          )}
        </TableContainer>
      );
    }

    if (activeTab === "Financial Insights") {
      const summary = financialInsights.summary || {};
      const periodData = financialInsights.periodData || [];
      const topCustomersByRevenue = financialInsights.topCustomersByRevenue || [];
      const topPartnersByVolume = financialInsights.topPartnersByVolume || [];
      const topCustomersByTalents = financialInsights.topCustomersByTalents || [];
      const topPartnersByTalents = financialInsights.topPartnersByTalents || [];
      const profitMargin = summary.totalReceivable ? ((summary.totalRevenue || 0) / summary.totalReceivable * 100) : 0;

      return (
        <InsightsGrid>
          <InsightCard>
            <h3>Top Customers by Revenue</h3>
            <div className="insight-content">
              {topCustomersByRevenue.length > 0 ? (
                topCustomersByRevenue.map((customer, index) => (
                  <div className="insight-row" key={index}>
                    <span className="label">{customer.name}</span>
                    <span className="value">₹ {((customer.revenue || 0) / 100000).toFixed(2)}L</span>
                  </div>
                ))
              ) : (
                <div className="insight-row">
                  <span className="label">{loadingFinancialInsights ? "Loading..." : "No data available"}</span>
                </div>
              )}
            </div>
          </InsightCard>

          <InsightCard>
            <h3>Top Partners by Volume</h3>
            <div className="insight-content">
              {topPartnersByVolume.length > 0 ? (
                topPartnersByVolume.map((partner, index) => (
                  <div className="insight-row" key={index}>
                    <span className="label">{partner.name}</span>
                    <span className="value">₹ {((partner.volume || 0) / 100000).toFixed(2)}L</span>
                  </div>
                ))
              ) : (
                <div className="insight-row">
                  <span className="label">{loadingFinancialInsights ? "Loading..." : "No data available"}</span>
                </div>
              )}
            </div>
          </InsightCard>

          <InsightCard>
            <h3>Top Customers by Talents Hired</h3>
            <div className="insight-content">
              {topCustomersByTalents.length > 0 ? (
                topCustomersByTalents.map((customer, index) => (
                  <div className="insight-row" key={index}>
                    <span className="label">{customer.name}</span>
                    <span className="value">{customer.count} Talent{customer.count !== 1 ? 's' : ''}</span>
                  </div>
                ))
              ) : (
                <div className="insight-row">
                  <span className="label">{loadingFinancialInsights ? "Loading..." : "No data available"}</span>
                </div>
              )}
            </div>
          </InsightCard>

          <InsightCard>
            <h3>Top Partners by Talents Deployed</h3>
            <div className="insight-content">
              {topPartnersByTalents.length > 0 ? (
                topPartnersByTalents.map((partner, index) => (
                  <div className="insight-row" key={index}>
                    <span className="label">{partner.name}</span>
                    <span className="value">{partner.count} Talent{partner.count !== 1 ? 's' : ''}</span>
                  </div>
                ))
              ) : (
                <div className="insight-row">
                  <span className="label">{loadingFinancialInsights ? "Loading..." : "No data available"}</span>
                </div>
              )}
            </div>
          </InsightCard>

          <InsightCard>
            <h3>Revenue vs Costs</h3>
            <div className="insight-content">
              <div className="insight-row">
                <span className="label">Total Revenue Generated</span>
                <span className="value" style={{ color: "#52c41a" }}>
                  ₹ {((summary.totalRevenue || 0) / 100000).toFixed(2)}L
                </span>
              </div>
              <div className="insight-row">
                <span className="label">Total Receivables</span>
                <span className="value">
                  ₹ {((summary.totalReceivable || 0) / 100000).toFixed(2)}L
                </span>
              </div>
              <div className="insight-row">
                <span className="label">Total Payables</span>
                <span className="value">
                  ₹ {((summary.totalPayable || 0) / 100000).toFixed(2)}L
                </span>
              </div>
              <div className="insight-row">
                <span className="label">Profit Margin</span>
                <span className="value" style={{ color: "#52c41a" }}>
                  {profitMargin.toFixed(2)}%
                </span>
              </div>
            </div>
          </InsightCard>

          <InsightCard>
            <h3>Payment Status Summary</h3>
            <div className="insight-content">
              <div className="insight-row">
                <span className="label">Invoices Fully Paid</span>
                <span className="value" style={{ color: "#52c41a" }}>
                  {summary.paidInvoices || 0}
                </span>
              </div>
              <div className="insight-row">
                <span className="label">Bills Fully Paid</span>
                <span className="value" style={{ color: "#52c41a" }}>
                  {summary.paidBills || 0}
                </span>
              </div>
              <div className="insight-row">
                <span className="label">Pending Receivables</span>
                <span className="value" style={{ color: "#ff4d4f" }}>
                  {summary.receivablePending || 0}
                </span>
              </div>
              <div className="insight-row">
                <span className="label">Pending Payables</span>
                <span className="value" style={{ color: "#ff4d4f" }}>
                  {summary.payablePending || 0}
                </span>
              </div>
            </div>
          </InsightCard>

          <InsightCard>
            <h3>GST Analysis</h3>
            <div className="insight-content">
              <div className="insight-row">
                <span className="label">Total GST IN (Collected)</span>
                <span className="value">
                  ₹ {((summary.totalGstIn || 0) / 100000).toFixed(2)}L
                </span>
              </div>
              <div className="insight-row">
                <span className="label">Total GST OUT (Paid)</span>
                <span className="value">
                  ₹ {((summary.totalGstOut || 0) / 100000).toFixed(2)}L
                </span>
              </div>
              <div className="insight-row">
                <span className="label">Net GST Liability</span>
                <span
                  className="value"
                  style={{
                    color:
                      (summary.totalGstIn || 0) - (summary.totalGstOut || 0) > 0
                        ? "#ff4d4f"
                        : "#52c41a",
                  }}
                >
                  ₹{" "}
                  {(
                    ((summary.totalGstIn || 0) - (summary.totalGstOut || 0)) /
                    100000
                  ).toFixed(2)}
                  L
                </span>
              </div>
              <div className="insight-row">
                <span className="label">GST Recovery Rate</span>
                <span className="value">
                  {summary.totalGstIn ? (
                    ((summary.totalGstOut || 0) / summary.totalGstIn) *
                    100
                  ).toFixed(2) : "0.00"}
                  %
                </span>
              </div>
            </div>
          </InsightCard>

          <InsightCard>
            <h3>Period Performance</h3>
            <div className="insight-content">
              {periodData.length > 0 ? (
                periodData.slice(0, 4).map((period, index) => (
                  <div className="insight-row" key={index}>
                    <span className="label">{period.period}</span>
                    <span className="value">₹ {((period.revenue || 0) / 100000).toFixed(2)}L</span>
                  </div>
                ))
              ) : (
                <div className="insight-row">
                  <span className="label">{loadingFinancialInsights ? "Loading..." : "No data available"}</span>
                </div>
              )}
            </div>
          </InsightCard>
        </InsightsGrid>
      );
    }

    return null;
  };

  return (
    <FinanceContainer>
      <PageHeader>
        <h1>Finance</h1>
        <p>Manage billing, payments, reconciliation, and financial operations</p>
      </PageHeader>

      {renderMetrics()}

      <TabsContainer>
        <Button
          className={`tab-button ${
            activeTab === "Partner Payables" ? "active" : ""
          }`}
          onClick={() => setActiveTab("Partner Payables")}
        >
          Partner Payables
        </Button>
        <Button
          className={`tab-button ${activeTab === "Client Billing" ? "active" : ""}`}
          onClick={() => setActiveTab("Client Billing")}
        >
          Client Billing
        </Button>
        <Button
          className={`tab-button ${
            activeTab === "Invoice Reconciliation" ? "active" : ""
          }`}
          onClick={() => setActiveTab("Invoice Reconciliation")}
        >
          Invoice Reconciliation
        </Button>
        <Button
          className={`tab-button ${
            activeTab === "Revenue Analysis" ? "active" : ""
          }`}
          onClick={() => setActiveTab("Revenue Analysis")}
        >
          Revenue Analysis
        </Button>
        <Button
          className={`tab-button ${
            activeTab === "Financial Insights" ? "active" : ""
          }`}
          onClick={() => setActiveTab("Financial Insights")}
        >
          Financial Insights
        </Button>
        <Button
          type="link"
          icon={<SyncOutlined spin={syncing} />}
          onClick={handleSyncNow}
          loading={syncing}
          style={{ marginLeft: 'auto', padding: 0, color: '#1890ff' }}
        >
          Sync Now
        </Button>
      </TabsContainer>

      {renderFilters()}

      {renderTable()}

      {/* Create Invoice Modal */}
      <Modal
        title={
          selectedInvoiceRecord?.invoiceStatus === "Rejected"
            ? `Modify & Re-issue Invoice - ${selectedInvoiceRecord?.talentName || ''}`
            : selectedInvoiceRecord?.invoiceStatus === "Sent" || selectedInvoiceRecord?.invoiceStatus === "Approved"
            ? `Edit Invoice - ${selectedInvoiceRecord?.talentName || ''}`
            : `Create Invoice - ${selectedInvoiceRecord?.talentName || ''}`
        }
        open={createInvoiceModalVisible}
        onOk={handleSubmitInvoice}
        onCancel={handleCloseInvoiceModal}
        width={700}
        okText={uploadingInvoice ? "Uploading..." : selectedInvoiceRecord?.invoiceStatus === "Rejected" ? "Modify & Re-issue" : "Create & Send Invoice"}
        okButtonProps={{ style: { background: "#00d9a9", borderColor: "#00d9a9" }, loading: uploadingInvoice, disabled: uploadingInvoice }}
      >
        {selectedInvoiceRecord && (
          <div style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 8 }}>
            {/* Talent Details */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                Invoice Details
              </label>
              <div style={{ padding: "12px 16px", background: "#f8f9fd", borderRadius: 6 }}>
                <div><strong>Partner Invoice Ref:</strong> {selectedInvoiceRecord.partnerInvoiceId || '-'}</div>
                <div><strong>Talent Name:</strong> {selectedInvoiceRecord.talentName}</div>
                <div><strong>Customer:</strong> {selectedInvoiceRecord.customer}</div>
                <div><strong>Month:</strong> {selectedInvoiceRecord.month} {selectedInvoiceRecord.year}</div>
                <div><strong>Days:</strong> {selectedInvoiceRecord.days} days</div>
                <div><strong>Hours:</strong> {selectedInvoiceRecord.hours} hours</div>
              </div>
            </div>

            {/* Invoice Number */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                Client Invoice Number <span style={{ color: "red" }}>*</span>
              </label>
              <Input
                placeholder="Enter client invoice number"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                style={{ width: "100%" }}
              />
            </div>

            {/* Amount Breakup */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                Amount Breakup
              </label>
              <div style={{ padding: 16, background: "#fff5e6", borderRadius: 6 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span>Base Amount ({selectedInvoiceRecord.days} days):</span>
                  <strong>₹ {(selectedInvoiceRecord.amount || 0).toLocaleString("en-IN")}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span>GST (18%):</span>
                  <strong>₹ {(selectedInvoiceRecord.gst || 0).toLocaleString("en-IN")}</strong>
                </div>
                <div style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  paddingTop: 8, 
                  borderTop: "2px solid #014c75",
                  marginTop: 8 
                }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: "#014c75" }}>Total Amount:</span>
                  <strong style={{ fontSize: 16, color: "#00d9a9" }}>
                    ₹ {(selectedInvoiceRecord.totalAmount || 0).toLocaleString("en-IN")}
                  </strong>
                </div>
              </div>
            </div>

            {/* Notes/Comments */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                Notes / Comments
              </label>
              <Input.TextArea
                placeholder="Enter any notes or comments for this invoice"
                value={invoiceNotes}
                onChange={(e) => setInvoiceNotes(e.target.value)}
                rows={3}
                style={{ width: "100%" }}
              />
            </div>

            {/* Invoice Upload */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                Upload Invoice <span style={{ color: "red" }}>*</span>
              </label>
              <Dragger
                name="file"
                multiple={false}
                accept=".pdf,.doc,.docx"
                beforeUpload={(file) => {
                  const isPDF = file.type === "application/pdf" ||
                               file.type === "application/msword" ||
                               file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
                  if (!isPDF) {
                    message.error("You can only upload PDF or Word documents!");
                    return Upload.LIST_IGNORE;
                  }
                  setUploadedInvoiceFile(file);
                  return false;
                }}
                onRemove={() => {
                  setUploadedInvoiceFile(null);
                }}
                fileList={uploadedInvoiceFile ? [uploadedInvoiceFile] : []}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined style={{ color: "#00d9a9" }} />
                </p>
                <p className="ant-upload-text">Click or drag invoice file to this area to upload</p>
                <p className="ant-upload-hint">
                  Support for PDF, DOC, or DOCX files. Upload the signed invoice document.
                </p>
              </Dragger>
            </div>
          </div>
        )}
      </Modal>

      {/* Partner Invoice Modal */}
      <Modal
        title={`${selectedPayableRecord?.partnerInvoiceId ? 'Edit' : 'Upload'} Partner Invoice - ${selectedPayableRecord?.talentName || ''}`}
        open={partnerInvoiceModalVisible}
        onOk={handleSubmitPartnerInvoice}
        onCancel={handleClosePartnerInvoiceModal}
        width={700}
        okText={uploadingInvoice ? "Uploading..." : (selectedPayableRecord?.partnerInvoiceId ? "Update Invoice" : "Submit Invoice")}
        okButtonProps={{ style: { background: "#1890ff", borderColor: "#1890ff" }, loading: uploadingInvoice, disabled: uploadingInvoice }}
      >
        {selectedPayableRecord && (
          <div style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 8 }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                Invoice Details
              </label>
              <div style={{ padding: "12px 16px", background: "#f8f9fd", borderRadius: 6 }}>
                <div><strong>Talent ID:</strong> {selectedPayableRecord.talentId}</div>
                <div><strong>Partner:</strong> {selectedPayableRecord.partner}</div>
                <div><strong>Month:</strong> {selectedPayableRecord.month} {selectedPayableRecord.year}</div>
                <div><strong>Partner Total:</strong> ₹ {(selectedPayableRecord.partnerTotal || 0).toLocaleString("en-IN")}</div>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                Invoice Number <span style={{ color: "red" }}>*</span>
              </label>
              <Input
                placeholder="Enter partner invoice number"
                value={partnerInvoiceNumber}
                onChange={(e) => setPartnerInvoiceNumber(e.target.value)}
                style={{ width: "100%" }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                Notes / Comments
              </label>
              <Input.TextArea
                placeholder="Enter any notes or comments"
                value={partnerInvoiceNotes}
                onChange={(e) => setPartnerInvoiceNotes(e.target.value)}
                rows={3}
                style={{ width: "100%" }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                Upload Invoice {!selectedPayableRecord?.partnerInvoiceId && <span style={{ color: "red" }}>*</span>}
              </label>
              <Dragger
                name="file"
                multiple={false}
                accept=".pdf,.doc,.docx"
                beforeUpload={(file) => {
                  const isPDF = file.type === "application/pdf" ||
                               file.type === "application/msword" ||
                               file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
                  if (!isPDF) {
                    message.error("You can only upload PDF or Word documents!");
                    return Upload.LIST_IGNORE;
                  }
                  setUploadedPartnerInvoiceFile(file);
                  return false;
                }}
                onRemove={() => {
                  setUploadedPartnerInvoiceFile(null);
                }}
                fileList={uploadedPartnerInvoiceFile ? [uploadedPartnerInvoiceFile] : []}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined style={{ color: "#1890ff" }} />
                </p>
                <p className="ant-upload-text">Click or drag invoice file to upload</p>
                <p className="ant-upload-hint">
                  Support for PDF, DOC, or DOCX files.
                </p>
              </Dragger>
            </div>
          </div>
        )}
      </Modal>

      {/* Modify Fee Modal */}
      <Modal
        title={`Modify Fee - ${selectedFeeRecord?.talentName || ''}`}
        open={modifyFeeModalVisible}
        onOk={handleSubmitModifyFee}
        onCancel={handleCloseModifyFeeModal}
        width={600}
        okText="Update Fee"
        okButtonProps={{ style: { background: "#00d9a9", borderColor: "#00d9a9" } }}
      >
        {selectedFeeRecord && (() => {
          const baseCost = selectedFeeRecord.baseCost || 0;
          const clientAmount = baseCost * (1 + clientFeePercentage / 100);
          const partnerAmount = baseCost * (1 - partnerFeePercentage / 100);
          const revenue = clientAmount - partnerAmount;
          
          return (
            <div style={{ paddingRight: 8 }}>
              {/* Talent Details */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                  Reconciliation Details
                </label>
                <div style={{ padding: "12px 16px", background: "#f8f9fd", borderRadius: 6 }}>
                  <div><strong>Talent ID:</strong> {selectedFeeRecord.talentId}</div>
                  <div><strong>Talent Name:</strong> {selectedFeeRecord.talentName}</div>
                  <div><strong>Partner:</strong> {selectedFeeRecord.partner}</div>
                  <div><strong>Customer:</strong> {selectedFeeRecord.customer}</div>
                  <div><strong>Job ID:</strong> {selectedFeeRecord.jobId}</div>
                  <div><strong>Month:</strong> {selectedFeeRecord.month} {selectedFeeRecord.year}</div>
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #d9d9d9" }}>
                    <strong>Talent Base Cost:</strong> ₹ {baseCost.toLocaleString("en-IN")}
                  </div>
                </div>
              </div>

              {/* Fee Percentages */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                  Fee Configuration
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div>
                    <label style={{ display: "block", marginBottom: 8, fontSize: 13 }}>
                      Client Fee (% Markup) <span style={{ color: "red" }}>*</span>
                    </label>
                    <InputNumber
                      value={clientFeePercentage}
                      onChange={(value) => setClientFeePercentage(Number(value) || 0)}
                      min={0}
                      max={100}
                      style={{ width: "100%" }}
                      formatter={(value) => `${value}%`}
                      parser={(value) => Number(value.replace('%', ''))}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", marginBottom: 8, fontSize: 13 }}>
                      Partner Fee (% Deduction) <span style={{ color: "red" }}>*</span>
                    </label>
                    <InputNumber
                      value={partnerFeePercentage}
                      onChange={(value) => setPartnerFeePercentage(Number(value) || 0)}
                      min={0}
                      max={100}
                      style={{ width: "100%" }}
                      formatter={(value) => `${value}%`}
                      parser={(value) => Number(value.replace('%', ''))}
                    />
                  </div>
                </div>
              </div>

              {/* Calculated Amounts */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                  Auto-Calculated Amounts
                </label>
                <div style={{ padding: 16, background: "#e6f7ff", borderRadius: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <span>Client Amount (Base + {clientFeePercentage}%):</span>
                    <strong style={{ color: "#014c75" }}>₹ {clientAmount.toLocaleString("en-IN")}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <span>Partner Amount (Base - {partnerFeePercentage}%):</span>
                    <strong style={{ color: "#014c75" }}>₹ {partnerAmount.toLocaleString("en-IN")}</strong>
                  </div>
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    paddingTop: 8, 
                    borderTop: "2px solid #014c75",
                    marginTop: 8 
                  }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: "#014c75" }}>Revenue:</span>
                    <strong style={{ fontSize: 16, color: "#00d9a9" }}>
                      ₹ {revenue.toLocaleString("en-IN")}
                    </strong>
                  </div>
                </div>
              </div>

              {/* Note */}
              <div style={{ padding: 12, background: "#fff5e6", borderRadius: 6, fontSize: 13 }}>
                <strong>Note:</strong> Fee percentages will be auto-populated from Admin Settings in the future. Client Fee is added to base cost (markup), Partner Fee is deducted from base cost.
              </div>
            </div>
          );
        })()}
      </Modal>
    </FinanceContainer>
  );
};

export default Finance;
